<script>
    import { onMount } from 'svelte';
    import { link } from 'svelte-spa-router';
    import { AppPageHeader } from '../../framework/components/app';

    onMount(() => {
        window.scrollTo(0, 0);
    });

    export let pageTitle = 'Learning Hub';
    
    const ACCENT_ROTATION = ['teal', 'orange', 'pink', 'purple', 'yellow', 'green'];

    const blogPosts = [
        {
            title: 'Why a Market Research Brief is Essential for Project Success',
            date: '14th October 2024',
            excerpt: 'A well-written market research brief serves as a roadmap, guiding all stakeholders on the project\'s goals, methodologies, and expected outcomes. Without it, the risk of misalignment, missed objectives and irrelevant insights increase significantly.',
            slug: 'market-research-brief-is-essential-for-project-success',
            icon: 'file alternate outline',
        },
        {
            title: 'The Unsung Heroes of Research Success',
            date: '7th October 2024',
            excerpt: 'In today\'s tech-centric market and user research landscape, it\'s easy to neglect the foundations of a successful research project. Yet, these elements are critical to ensure insights generated are meaningful and actionable.',
            slug: 'the-unsung-heroes-of-research-success',
            icon: 'star outline',
        },
        {
            title: 'Optimizing User and Market Research with the Right Tools',
            date: '4th October 2024',
            excerpt: 'Despite the promise of agility and improved insights, the prospect of losing time and money when the wrong tech is chosen or used incorrectly remains a concern. Hence, navigating this expanding tech landscape is crucial for making informed decisions on the right research tech to use for your next project.',
            slug: 'optimizing-user-and-market-research-with-the-right-tools',
            icon: 'cogs',
        },
        {
            title: 'Translating The Marketing Problem Into A Well-defined Research Problem',
            date: '1st October 2024',
            excerpt: 'Thanks to technology, launching a market research project is fast and easy. Almost any question can be asked. With a few clicks and swipes, clients can expect responses from consumers. Whether the data collected and analyzed serves a marketing purpose remains to be seen.',
            slug: 'translating-the-marketing-problem-into-a-well-defined-research-problem',
            icon: 'lightbulb outline',
        }
    ].map((p, i) => ({ ...p, accent: ACCENT_ROTATION[i % ACCENT_ROTATION.length] }));
</script>

<div class="learning-hub-page">
    <AppPageHeader
        title={pageTitle}
        subtitle="Insights and best practices for market research"
        showAction={false}
    />

    <div class="blog-posts-grid">
        {#each blogPosts as post}
            <article
                class="blog-card"
                style="--accent-color: var(--accent-{post.accent}); --accent-color-soft: var(--accent-{post.accent}-soft);"
            >
                <div class="blog-card-accent"></div>
                <div class="blog-card-icon-wrapper">
                    <i class="{post.icon} icon blog-card-icon" aria-hidden="true"></i>
                </div>
                <div class="blog-card-content">
                    <div class="blog-card-header">
                        <h3 class="blog-title">{post.title}</h3>
                        <div class="blog-meta">
                            <i class="calendar outline icon"></i>
                            <time datetime={post.date}>{post.date}</time>
                        </div>
                    </div>
                    <div class="blog-excerpt">
                        <p>{post.excerpt}</p>
                    </div>
                    <div class="blog-card-footer">
                        <a use:link href="/blog/{post.slug}" class="read-more-button">
                            Read Article
                            <i class="arrow right icon"></i>
                        </a>
                    </div>
                </div>
            </article>
        {/each}
    </div>
</div>

<style>
    .learning-hub-page {
        width: 100%;
        min-height: 100vh;
        padding: var(--space-5) var(--space-6);
    }

    .blog-posts-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 1.5rem;
    }

    .blog-card {
        background: var(--white);
        border-radius: 12px;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        display: flex;
        flex-direction: column;
        transition: box-shadow 0.3s ease, transform 0.3s ease;
        overflow: hidden;
        position: relative;
    }

    .blog-card:hover {
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        transform: translateY(-4px);
    }

    .blog-card-accent {
        width: 100%;
        height: 6px;
        background: linear-gradient(135deg, var(--accent-color), var(--accent-color));
        opacity: 0.9;
    }

    .blog-card-icon-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1.5rem 1.5rem 0 1.5rem;
    }

    .blog-card-icon {
        font-size: 3rem;
        color: var(--accent-color);
        opacity: 0.8;
    }

    .blog-card-content {
        padding: 1rem 1.5rem 1.5rem 1.5rem;
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .blog-card-header {
        margin-bottom: 1rem;
    }

    .blog-title {
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.75rem 0;
        line-height: 1.4;
    }

    .blog-meta {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--grey-secondary);
    }

    .blog-meta i {
        font-size: 1rem;
    }

    .blog-excerpt {
        flex: 1;
        margin-bottom: 1.5rem;
    }

    .blog-excerpt p {
        color: var(--grey);
        line-height: 1.6;
        margin: 0;
        font-size: 0.9375rem;
    }

    .blog-card-footer {
        display: flex;
        justify-content: flex-end;
        padding-top: 1rem;
        margin-top: auto;
    }

    .read-more-button {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem 1.25rem;
        color: var(--white);
        background: var(--accent-color);
        text-decoration: none;
        font-weight: 500;
        font-size: 0.9375rem;
        border-radius: 6px;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .read-more-button:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        filter: brightness(1.1);
    }

    .read-more-button i {
        margin: 0;
        font-size: 1rem;
    }

    @media (max-width: 768px) {
        .learning-hub-page {
            padding: var(--space-4);
        }
        .blog-posts-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
        }

        .blog-card {
            padding: 1.25rem;
        }
    }
</style>

